import { useTranslation } from 'next-i18next'
import { useAppSelector } from 'src/app/hooks'
import styles from './OrderItemsList.module.scss'
import { Order, OrderItem } from 'src/core/models'
import { selectCurrencyToken } from 'src/features'
import { Button, Tooltip, Row, Divider } from 'antd'
import { CloudDownloadOutlined, EyeOutlined } from '@ant-design/icons'
import { getFirstSubstring, getFormatedPrice, getProjectKind, getProjectUnitUd } from 'src/core/helpers'
import { FormLabels, CustomStatusColumn, CustomPictureLabels, CustomCol, CustomTitle } from 'src/components'
import React from 'react'
import Link from 'next/link'
const marketUrl = process.env.MARKET_PLACE_URL

interface OrderItemsListProps {
  orderItems: OrderItem[]
  order: Order
}

const OrderItemsList: React.FC<OrderItemsListProps> = ({ orderItems, order }) => {
  const { t } = useTranslation()
  const currency_code = useAppSelector(selectCurrencyToken)

  const getProjectName = (item: OrderItem): string => {
    const name = `${getFirstSubstring(item.project_name, 16)}...`
    return item.is_vintage ? `${name} (${item.year})` : name
  }

  const getPrice = (item: OrderItem): string => {
    const price = !item.is_free_donation ? item?.total_amount : item.amount
    return getFormatedPrice(price, currency_code)
  }

  const certificateIcon = (item: OrderItem) => {
    const isDisabled = order?.status === 'pending'
    const title = isDisabled ? t('Transactions.UnableCertificate') : t('Transactions.DownloadCertificate')
    const color = isDisabled ? 'red' : 'blue'
    return (
      <a target='_blank' rel='noopener noreferrer' href={item.certificate}>
        <Tooltip title={title} color={color}>
          <Button
            type='default'
            shape='circle'
            className='radius-100 mr-5'
            disabled={isDisabled}
            icon={<CloudDownloadOutlined />}
          />
        </Tooltip>
      </a>
    )
  }

  const manageOrderItemStatus = (status: string) => {
    if (status === '') {
      status = 'paid'
    }
    return status === 'paid'
  }

  const checkDetailsIcon = (item: OrderItem) => {
    return (
      <Link href={`/my-orders/details?order=${order.id}&order_item=${item.id}`} passHref legacyBehavior>
        <a>
          <Tooltip title={t('Transactions.CheckOrderItemDetails')} color={'blue'}>
            <Button
              type='default'
              shape='circle'
              className='radius-100 ml-4'
              icon={<EyeOutlined />}
            />
          </Tooltip>
        </a>
      </Link>
    )
  }

  return (
    <div className='collapse-animation'>
      {orderItems.map((orderItem: OrderItem) => <div key={orderItem.id}>
        <Row>
          {/* Project Labels */}
          <CustomCol lg={10}>
            <CustomPictureLabels
              imageUrl={orderItem.project_thumbnail}
              title={getProjectName(orderItem)}
              subtitle={getProjectKind(orderItem.project_kind, t)}
              link={`${marketUrl}/projects?id=${orderItem.project_code}`}
            />
          </CustomCol>

          {/* Labels */}
          <CustomCol
            xs={8} lg={4}
            className={styles.labels}
          >
            {orderItem.is_free_donation ? (
              <CustomTitle
                label={getPrice(orderItem)}
                type={null}
              />
            ) : (
              <FormLabels
                title={`${getPrice(orderItem)}`}
                subtitle={`
                      ${orderItem.amount}
                      ${getProjectUnitUd(orderItem.project_kind, t, orderItem.amount)}
                    `}
              />
            )}
          </CustomCol>

          {/* Status */}
          <CustomCol
            xs={8} lg={5}
            className={styles.status}
          >
            <CustomStatusColumn
              isStatusOk={manageOrderItemStatus(orderItem.status)}
              isCancelled={order.is_cancelled}
            />
          </CustomCol>

          {/* Actions */}
          <CustomCol
            lg={5}
            className={styles.actions}
          >
            {certificateIcon(orderItem)}
            {checkDetailsIcon(orderItem)}
          </CustomCol>
        </Row>
        {orderItems.length > 1 && <Divider />}
      </div>)}
    </div>
  )
}

export default OrderItemsList
